import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { DEFAULT_THEME, type Theme } from '@dashboard/contracts';

/**
 * Theming by design token (section 17).
 *
 * The server delivers a validated token document; this projects it onto CSS
 * custom properties on the root element. Components then reference variables
 * only -- no component ever imports a brand color, which is what makes a client
 * rebrand configuration rather than a fork (principle 5.6).
 */

export type ColorMode = 'light' | 'dark' | 'system';

interface ThemeContextValue {
  theme: Theme;
  mode: ColorMode;
  resolvedMode: 'light' | 'dark';
  setMode: (mode: ColorMode) => void;
  /** Series colors for the resolved mode, already stepped for that surface. */
  chartColors: string[];
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Exported because `index.html` reads the same key from an inline script that
 * resolves the colour mode before first paint, and cannot import it.
 * `theme-init.test.ts` asserts the two still agree -- renaming this silently
 * reintroduces the flash otherwise.
 */
export const MODE_STORAGE_KEY = 'dashboard.colorMode';

/**
 * Whether a DOM is present.
 *
 * The provider is rendered without one in component tests, which render to
 * static markup in Node. Reaching for `window` unguarded made the provider --
 * and therefore every themed component -- impossible to render outside a
 * browser, which is a real limitation rather than only a test inconvenience.
 * Both reads below degrade to the same defaults a first-time visitor gets.
 */
const hasDom = typeof window !== 'undefined';

function readStoredMode(): ColorMode {
  // A display preference, not a credential: local storage is the right place
  // for it, unlike anything session-bearing (section 12.3).
  if (!hasDom) return 'system';
  const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
}

function useSystemPrefersDark(): boolean {
  const [prefersDark, setPrefersDark] = useState(
    () => hasDom && window.matchMedia('(prefers-color-scheme: dark)').matches,
  );

  useEffect(() => {
    if (!hasDom) return;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = (event: MediaQueryListEvent) => setPrefersDark(event.matches);
    query.addEventListener('change', listener);
    return () => query.removeEventListener('change', listener);
  }, []);

  return prefersDark;
}

export function ThemeProvider({ theme, children }: { theme?: Theme; children: ReactNode }) {
  const [mode, setModeState] = useState<ColorMode>(readStoredMode);
  const prefersDark = useSystemPrefersDark();

  const activeTheme = theme ?? DEFAULT_THEME;
  const resolvedMode: 'light' | 'dark' =
    mode === 'system' ? (prefersDark ? 'dark' : 'light') : mode;

  const setMode = (next: ColorMode) => {
    if (hasDom) window.localStorage.setItem(MODE_STORAGE_KEY, next);
    setModeState(next);
  };

  useEffect(() => {
    if (!hasDom) return;
    const root = document.documentElement;
    const colors = activeTheme.colors[resolvedMode];

    for (const [name, value] of Object.entries(colors)) {
      root.style.setProperty(`--color-${kebab(name)}`, value);
    }

    activeTheme.chartPalette[resolvedMode].forEach((color, index) => {
      root.style.setProperty(`--series-${index + 1}`, color);
    });

    root.style.setProperty('--font-family', activeTheme.typography.fontFamily);
    root.style.setProperty('--font-mono', activeTheme.typography.monoFamily);
    root.style.setProperty('--font-size-base', `${activeTheme.typography.baseSize}px`);
    root.style.setProperty('--radius', `${activeTheme.shape.radius}px`);
    root.style.setProperty(
      '--density-scale',
      activeTheme.shape.density === 'compact' ? '0.8' : '1',
    );

    root.dataset.theme = resolvedMode;
    // Tells the browser to style form controls and scrollbars to match, which
    // is otherwise the giveaway that a "dark mode" is only skin deep.
    root.style.colorScheme = resolvedMode;
  }, [activeTheme, resolvedMode]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme: activeTheme,
      mode,
      resolvedMode,
      setMode,
      chartColors: activeTheme.chartPalette[resolvedMode],
    }),
    [activeTheme, mode, resolvedMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside ThemeProvider');
  return context;
}

function kebab(value: string): string {
  return value.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
}
